import { asteroidWallSpinMassFactor, bounceCircleOffWalls, wrap, normalizeDeg } from '../physics.js';
import {
  ASTEROID_BONUS_LARGE,
  ASTEROID_BONUS_MEDIUM,
  ASTEROID_BONUS_SMALL,
  ASTEROID_BONUS_TINY,
  ASTEROID_MIN_SPLIT_RADIUS,
  ASTEROID_START_SIZE,
  ASTEROID_VERTEX_COUNT,
  ASTEROID_WALL_RESTITUTION, ASTEROID_WALL_SPIN_COUPLING, ASTEROID_WALL_TANGENTIAL_FRICTION
} from '../constants.js';
import { asteroidVertices } from '../helpers.js';

/**
 * Ballistic asteroid. Shape is deterministic from asteroid id + radius, so
 * any client can reconstruct vertices from authoritative snapshot state.
 */
export default class Asteroid {
  constructor({
    id, position, velocity, rotation = 0, rotationSpeed,
    radius, vertices, score
  }) {
    this.id = id;
    this.position = { x: position.x, y: position.y };
    this.velocity = { x: velocity.x, y: velocity.y };
    this.rotation = rotation;
    this.rotationSpeed = rotationSpeed;
    this.radius = radius;
    this.vertices = vertices ?? asteroidVertices(id, ASTEROID_VERTEX_COUNT, radius);
    this.score = score ?? (
      radius === ASTEROID_START_SIZE ? ASTEROID_BONUS_LARGE
        : radius === ASTEROID_START_SIZE / 2 ? ASTEROID_BONUS_MEDIUM
          : radius === ASTEROID_START_SIZE / 4 ? ASTEROID_BONUS_SMALL
            : radius === ASTEROID_START_SIZE / 8 ? ASTEROID_BONUS_TINY
              : ASTEROID_BONUS_LARGE
    )
  }

  // Only split if both children are at least ASTEROID_MIN_SPLIT_RADIUS.
  canSplit() {
    return this.radius >= ASTEROID_MIN_SPLIT_RADIUS * 2;
  }

  update(dt, worldBounds) {
    this.position.x += this.velocity.x * dt;
    this.position.y += this.velocity.y * dt;
    this.rotation = normalizeDeg(this.rotation + this.rotationSpeed * dt);
    if (worldBounds.toroidalMap === false) {
      bounceCircleOffWalls(this, worldBounds, {
        restitution: ASTEROID_WALL_RESTITUTION,
        tangentialFriction: ASTEROID_WALL_TANGENTIAL_FRICTION,
        spinCoupling: ASTEROID_WALL_SPIN_COUPLING,
        spinMassFactor: asteroidWallSpinMassFactor(this.radius),
        spinDamping: 0.985
      });
    } else {
      /**
       * Keep asteroid centres in canonical world space for smooth interpolation
       * and seam continuity. Radius-padded wrap causes 2*radius centre jumps.
       */
      wrap(this.position, worldBounds.width, worldBounds.height, 0);
    }
  }
}
